import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatAUD, formatNumber } from "@/lib/format";
import { PageHeader, Loading } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { ShoppingCart, Users, Package, PackageCheck, Wallet, AlertCircle,
  TrendingUp, TrendingDown, DollarSign, Receipt, Truck, CircleDollarSign } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

function Kpi({ icon: Icon, label, value, tone = "default", testId }) {
  const toneCls = {
    default: "text-primary bg-primary/10",
    success: "text-[hsl(var(--success))] bg-[hsl(var(--success-bg))]",
    warning: "text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))]",
    danger: "text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))]",
  }[tone];
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${toneCls}`}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-heading text-xl font-semibold tracking-tight mt-0.5">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { selectedTripId, tripParam } = useApp();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/dashboard", { params: { trip_id: selectedTripId } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [selectedTripId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;
  if (!data) return null;

  const chartData = Object.entries(data.status_counts || {}).map(([name, value]) => ({ name, value }));
  const COLORS = ["#0D9488", "#F59E0B", "#3B82F6", "#6366F1", "#10B981", "#EF4444", "#0EA5E9", "#F97316"];

  return (
    <div>
      <PageHeader title={`Halo, ${user?.display_name || user?.name || "Pengguna"}`} subtitle={data.active_trip ? `Trip aktif: ${data.active_trip.name}` : "Ringkasan bisnis jastip Anda"} />

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4">
        <Kpi icon={ShoppingCart} label="Total Order" value={formatNumber(data.total_orders)} testId="kpi-orders" />
        <Kpi icon={Users} label="Total Pelanggan" value={formatNumber(data.total_customers)} testId="kpi-customers" />
        <Kpi icon={Package} label="Produk Diminta" value={formatNumber(data.total_products_requested)} testId="kpi-products" />
        <Kpi icon={PackageCheck} label="Produk Ditemukan" value={formatNumber(data.total_products_found)} tone="success" testId="kpi-found" />
        <Kpi icon={Wallet} label="Pembayaran Masuk" value={formatIDR(data.total_customer_payments)} tone="success" testId="kpi-payments" />
        <Kpi icon={AlertCircle} label="Piutang" value={formatIDR(data.outstanding_payments)} tone="danger" testId="kpi-outstanding" />
        <Kpi icon={Truck} label="Order Terkirim" value={formatNumber(data.total_products_delivered)} testId="kpi-delivered" />
        <Kpi icon={CircleDollarSign} label="Order Lunas" value={formatNumber(data.total_products_paid)} tone="success" testId="kpi-paid" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-5">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5 shadow-sm">
          <p className="font-heading font-medium mb-4">Status Order</p>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ left: -10 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={70} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((e, i) => <Cell key={e.name} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground py-10 text-center">Belum ada order</p>}
        </div>

        <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <p className="font-heading font-medium mb-4">Ringkasan Keuangan</p>
          <div className="space-y-3">
            <FinRow icon={DollarSign} label="Total Revenue" value={formatIDR(data.total_revenue)} />
            <FinRow icon={Package} label="Biaya Beli (AUD)" value={formatAUD(data.total_purchase_aud)} />
            <FinRow icon={Package} label="Biaya Beli (IDR)" value={formatIDR(data.total_purchase_idr)} />
            <FinRow icon={Receipt} label="Total Pengeluaran" value={formatIDR(data.total_expenses)} />
            <div className="border-t border-border pt-3 space-y-3">
              <FinRow icon={TrendingUp} label="Est. Laba Kotor" value={formatIDR(data.gross_profit)} tone={data.gross_profit >= 0 ? "success" : "danger"} />
              <FinRow icon={data.net_profit >= 0 ? TrendingUp : TrendingDown} label="Est. Laba Bersih" value={formatIDR(data.net_profit)} tone={data.net_profit >= 0 ? "success" : "danger"} strong />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FinRow({ icon: Icon, label, value, tone, strong }) {
  const c = tone === "success" ? "text-[hsl(var(--success))]" : tone === "danger" ? "text-[hsl(var(--danger))]" : "";
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm text-muted-foreground"><Icon className="h-4 w-4" />{label}</span>
      <span className={`font-mono tabular-nums text-sm ${strong ? "font-semibold text-base" : ""} ${c}`}>{value}</span>
    </div>
  );
}
